import React from "react";

/**
 * Catches render-time errors thrown inside ChatPane (or any subtree it wraps)
 * so a single broken message render or a malformed API payload doesn't blank
 * out the whole workspace shell. Renders a friendly inline fallback with a
 * "Try again" button that re-mounts the children (by toggling key).
 *
 * Usage:
 *   <ChatErrorBoundary>
 *     <ChatPane ... />
 *   </ChatErrorBoundary>
 */
export default class ChatErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, attempt: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ChatErrorBoundary caught render error:", error, info);
  }

  reset = () => {
    this.setState((s) => ({ hasError: false, attempt: s.attempt + 1 }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex-1 flex items-center justify-center bg-[#FAF9F7] px-6"
          data-testid="chat-pane-error-boundary"
        >
          <div className="max-w-md text-center">
            <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#A8351F] font-semibold mb-3">
              Something went wrong
            </p>
            <h2 className="font-serif text-2xl text-[#2C2A29] mb-3">
              We couldn&rsquo;t render this chat.
            </h2>
            <p className="font-sans text-sm text-[#5E5B58] leading-relaxed mb-6">
              The page hit an unexpected error while loading. Try again, or
              refresh the page if it keeps happening.
            </p>
            <button
              type="button"
              onClick={this.reset}
              data-testid="chat-pane-error-retry"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#2C2A29] text-white font-sans text-sm font-medium hover:bg-[#1A1918] transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    // The `key` swap forces React to unmount + remount the subtree on retry,
    // clearing any stale state in the children.
    return (
      <React.Fragment key={this.state.attempt}>
        {this.props.children}
      </React.Fragment>
    );
  }
}
